import Component from '@glimmer/component';

import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';

import concatClasses from 'rise-ui/helpers/concat-classes.js';
import { call } from 'rise-ui/utils/function.js';
import { propDefaultsTo } from 'rise-ui/utils/nullish.js';

/**
 * @property {string} [iconClass] Additional class for icon element
 */
export default class ExpandingBlockComponent extends Component {
  @tracked lazyBody = true;
  @tracked removeBody = false;

  @tracked _expanded = this.args.initExpanded;
  get expanded() {
    return propDefaultsTo(this.args, 'expanded', this._expanded);
  }

  get stateClass() {
    return this.expanded ? 'expanded' : 'collapsed';
  }

  _hasExpanded = false;
  get hasExpanded() {
    return this._hasExpanded = this._hasExpanded || this.expanded;
  }

  get renderBody() {
    const { hasExpanded } = this;
    return !this.lazyBody || this.expanded || hasExpanded && !this.removeBody;
  }

  get text() {
    const { args: { disabled, disabledAddText, disabledText }, expanded } = this;
    let text;
    if (disabled && typeof disabledText === 'string') {
      text = disabledText;
    } else {
      text = expanded ? 'Collapse' : 'Expand';
      if (disabled) {
        text += ' (Disabled';
        if (typeof disabledAddText === 'string') {
          text += ': ' + disabledAddText;
        }
        text += ')';
      }
    }
    return text;
  }

  emitToggleEvents() {
    if (this._expanded) {
      this.emitOnExpand();
    } else {
      this.emitOnCollapse();
    }
    this.emitOnToggle();
  }

  emitOnToggle() {
    call(this.args.onToggle, null, this._expanded);
  }

  emitOnExpand() {
    call(this.args.onExpand, null, this._expanded);
  }

  emitOnCollapse() {
    call(this.args.onCollapse, null, this._expanded);
  }

  @action
  headExpand() {
    if (this.args.noHeadExpand) {
      return;
    }
    this.expand();
  }

  @action
  buttonExpand() {
    this.expand();
  }

  @action
  expand() {
    if (this.args.disabled) {
      return;
    }
    this._expanded = !this.expanded;
    this.emitToggleEvents();
  }

  <template>
    {{#let (uniqueId) as | controlId |}}
    <div class="c-expands {{this.stateClass}}" ...attributes>
      {{#if @button}}
        <div
          class={{concatClasses
            "c-expands__icon"
            @iconClass
            this.stateClass
            (if @disabled "disabled")
          }}
          title={{this.text}}
          {{on "click" this.buttonExpand}}
        ></div>
      {{/if}}
      <div class="c-expands__group {{this.stateClass}}">
        <div
          class="c-expands__head {{this.stateClass}}"
          title={{unless @button this.text}}
          {{on "click" this.headExpand}}
        >
          {{yield to="head"}}
        </div>
        <div id={{controlId}} class="c-expands__body {{this.stateClass}}">
          {{#if this.renderBody}}
            {{yield to="body"}}
          {{/if}}
        </div>
      </div>
    </div>
    {{/let}}
  </template>
}
